import React from 'react';
import { styled } from '@components-shared/src/stitches';
import { LOADING_STYLES } from '@components/main-components/skeleton/Skeleton';
import { InfoTooltip } from '@components/main-components/tooltip/InfoTooltip';
import { MediaQuery } from '@context/mediaQuery';
import { useChartCardContext } from './Context';

const STATS = [
  {
    name: 'current',
    label: 'Current',
    tooltipContent: 'This week’s ranking',
    rank: true,
  },
  {
    name: 'last',
    label: 'Last week',
    tooltipContent: 'Last week’s ranking',
    rank: true,
  },
  {
    name: 'peak',
    label: 'Peak',
    tooltipContent: 'The highest ranking received',
    rank: true,
  },
  {
    name: 'streak',
    label: 'Streak',
    tooltipContent: 'Consecutive weeks on the chart',
    rank: false,
  },
] as const;

export function ChartCardStats(props: {
  /**
   * Current stat in chart
   */
  current: number;
  /**
   * Last week's stat in chart
   */
  last?: number;
  /**
   * Highest achieved rank in chart until current week
   */
  peak: number;
  /**
   * number of weeks the release has been in the chart without interruptions
   */
  streak?: number;
  /**
   * Whether the tooltip should be open by default. False by default.
   */
  initialOpen?: boolean;
}) {
  const { isLoadingDetails } = useChartCardContext();
  const { tabletLandscape } = MediaQuery.useContainer();

  return (
    <Container>
      {STATS.map(stat => {
        const empty = !props[stat.name];
        return (
          <Stat key={stat.name}>
            <Label>
              <LabelText>{stat.label}</LabelText>
              <InfoTooltip
                openOnClick={!tabletLandscape}
                variant="outlined"
                size={12}
                initialOpen={props.initialOpen}
              >
                <InfoTooltip.Content>{stat.tooltipContent}</InfoTooltip.Content>
              </InfoTooltip>
            </Label>

            <Value loading={isLoadingDetails} rank={stat.rank && !empty} empty={empty}>
              {!empty && props[stat.name]}
            </Value>
          </Stat>
        );
      })}
    </Container>
  );
}

const Container = styled('div', {
  width: '100%',
  display: 'flex',
  pointerEvents: 'all',
  flexShrink: 0,
  justifyContent: 'space-between',
  alignItems: 'center',
  '@tablet': {
    maxWidth: 480,
  },
});

const Stat = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  title: 'M',
  lineHeight: '34px',

  '@tablet': {
    title: 'L',
    lineHeight: '40px',
  },
});

const Label = styled('div', {
  display: 'flex',
  alignItems: 'center',
  fontSize: 11,
  gap: 4,
});

const LabelText = styled('div', {
  textTransform: 'uppercase',
  base: 'XS',
  fontWeight: 400,
});

const Value = styled('div', {
  title: 'XXS',

  '@tablet': {
    title: 'M',
  },

  variants: {
    loading: {
      true: {
        ...LOADING_STYLES,
        height: 12,
        width: 24,
        borderRadius: '$extraLarge',
        marginY: 6,
        color: 'transparent',
      },
    },
    rank: {
      true: {
        '&:before': {
          content: '"#"',
        },
      },
    },
    empty: {
      true: {
        '&:before': {
          content: '"—"',
        },
      },
    },
  },
});
